import { useCallback, useEffect, useState } from "react";
import {
  HiOutlineIdentification,
  HiHome,
  HiUsers,
  HiFolderOpen,
  HiOutlineCollection,
  HiOutlineCalendar,
  HiStop,
  HiOutlineSun,
  HiDotsCircleHorizontal,
  HiStar,
  HiOutlineClock,
} from "react-icons/hi"; //https://react-icons.github.io/react-icons/icons/hi/
import { Drawer, Sidebar, Tooltip } from "flowbite-react";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import { MdTag } from "react-icons/md";
import { SitePage } from "../../Models/SitePage";
import SitePageApi from "../../Api/SitePageApi";
import { Link, useLocation } from "react-router-dom";
import MemberApi from "../../Api/MemberApi";
import Loader from "../Common/Loader";
import { useConferenceData } from "../Conference/useConferenceData";
import EventSponsorApi from "../../Api/EventSponsorApi";
import AwardVotingApi from "../../Api/AwardVotingApi";

export default function SideNav({
  show,
  handleClose,
}: {
  show: boolean;
  handleClose: () => void;
}) {
  const { userModel } = useAppSelector((state) => state.user);
  const [pagesData, setPagesData] = useState<SitePage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hideParticipants = false; // Easy toggle for participants visibility
  const hideSpringParticipants = false; // Easy toggle for participants visibility
  const { springConference, fallConference, springSubEvents, fallSubEvents } =
    useConferenceData();
  const [hasFallSponsors, setHasFallSponsors] = useState(false);
  const [hasSpringSponsors, setHasSpringSponsors] = useState(false);
  const [awardsVotingOpen, setAwardsVotingOpen] = useState(false);

  // Only check conference-level sponsors (2 API calls total). Sub-event sponsors
  // are still shown when the user opens the Sponsors page.
  const checkConferenceSponsors = useCallback(async () => {
    const hasSponsors = async (
      conferenceId: number | undefined,
    ): Promise<boolean> => {
      if (!conferenceId) return false;
      try {
        const res = await EventSponsorApi.getByEventId(conferenceId);
        return !!(res.data && res.data.length > 0);
      } catch {
        return false;
      }
    };
    const [fall, spring] = await Promise.all([
      hasSponsors(fallConference?.id),
      hasSponsors(springConference?.id),
    ]);
    setHasFallSponsors(fall);
    setHasSpringSponsors(spring);
  }, [fallConference?.id, springConference?.id]);

  useEffect(() => {
    if (fallConference || springConference) {
      checkConferenceSponsors();
    }
  }, [fallConference, springConference, checkConferenceSponsors]);

  useEffect(() => {
    getSitePages();
    AwardVotingApi.isVotingOpen()
      .then((res) => setAwardsVotingOpen(!!res.data?.isOpen))
      .catch(() => setAwardsVotingOpen(false));
  }, []);
  const location = useLocation();

  useEffect(() => {
    if (show) handleClose(); // close on path change
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const getSitePages = () => {
    try {
      return SitePageApi.getSitePageListForMenu()
        .then((res) => {
          // Exclude Conference Info (pageTypeId 2) from the resources menu
          const menuPages = (res.data ?? []).filter((p) => p.pageTypeId !== 2);
          setPagesData(menuPages);
        })
        .catch((err) => {});
    } catch (error) {
      console.error(error);
    }
  };

  function checkAdminAccess() {
    if (userModel && userModel.userLevelId === UserLevelNames.SuperAdmin) {
      return true;
    }
    return false;
  }

  const providerPages = (pagesData ?? []).filter((p) => {
    // Primary: member-linked pages from updated API payload.
    const isMemberLinked = p.memberId != null;
    // Fallback: older payloads may omit memberId/showInNav but still include PageType.
    const isMemberType = (p.pageType?.name ?? "").toLowerCase().includes("member");
    // Menu endpoint already returns only active member pages; if flag is missing, treat as active.
    const isActive = p.showInNav ?? true;
    return (isMemberLinked || isMemberType) && isActive;
  });
  const providerIds = new Set(providerPages.map((p) => p.id));
  const resourcePages = (pagesData ?? []).filter((p) => !providerIds.has(p.id));

  return (
    <>
      {isLoading && <Loader />}
      <Drawer open={show} onClose={handleClose} className="block lg:hidden">
        <Drawer.Header title="Menu" />
        <Drawer.Items>
          <Sidebar className="[&>div]:bg-transparent [&>div] w-full h-full">
            <Sidebar.Items>
              <Sidebar.ItemGroup>
                <div className="flex content-center">
                  <Link to="/" className="w-full">
                    <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiHome />
                      <span>Dashboard</span>
                    </div>
                  </Link>
                </div>
                <div className="flex content-center">
                  <Link to="/schedule" className="w-full">
                    <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiOutlineClock />
                      <span>My Schedule</span>
                    </div>
                  </Link>
                </div>
                <div className="flex content-center">
                  <Link to="/members" className="w-full">
                    <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiOutlineCollection />
                      <span>Member List</span>
                    </div>
                  </Link>
                </div>
                {userModel &&
                  userModel.userLevelId !== UserLevelNames.SuperAdmin && (
                    <>
                      <div className="flex content-center">
                        <Sidebar.Item
                          href={`/member/${userModel?.memberId}`}
                          icon={HiOutlineIdentification}
                        >
                          <div className="">Member Profile</div>
                        </Sidebar.Item>
                      </div>
                      <div className="flex content-center">
                        <Sidebar.Item
                          href="/member/my-page"
                          icon={HiFolderOpen}
                        >
                          <div className="">My Showcase Page</div>
                        </Sidebar.Item>
                      </div>
                    </>
                  )}
                <div className="flex content-center">
                  <Sidebar.Collapse
                    icon={HiOutlineCalendar}
                    label={"Fall Convention"}
                  >
                    <Link
                      to={
                        fallConference?.sitePageId
                          ? `/page/${fallConference.sitePageId}`
                          : `/members/fallconvention`
                      }
                    >
                      <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                        Information
                      </div>
                    </Link>
                    <Link to="/members/industryshowcase">
                      <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                        <span>Industry Showcase</span>
                      </div>
                    </Link>
                    {!hideParticipants && fallConference && (
                      <Link to={`/events/${fallConference.id}/participants`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          <span>Convention Participants</span>
                        </div>
                      </Link>
                    )}
                    {fallConference && (
                      <Link to={`/events/${fallConference.id}/schedule`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          <span>Schedule</span>
                        </div>
                      </Link>
                    )}
                    {hasFallSponsors && fallConference && (
                      <Link to={`/events/${fallConference.id}/sponsors`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          <span>Sponsors</span>
                        </div>
                      </Link>
                    )}
                  </Sidebar.Collapse>
                </div>
                <div className="flex content-center">
                  <Sidebar.Collapse
                    icon={HiOutlineSun}
                    label={"Spring Meeting"}
                  >
                    <Link
                      to={
                        springConference?.sitePageId
                          ? `/page/${springConference.sitePageId}`
                          : `/members/springmeeting`
                      }
                    >
                      <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                        Meeting Information
                      </div>
                    </Link>
                    {!hideSpringParticipants && springConference && (
                      <Link to={`/events/${springConference.id}/participants`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          <span>Participants</span>
                        </div>
                      </Link>
                    )}
                    {springConference && (
                      <Link to={`/events/${springConference.id}/schedule`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          <span>Schedule</span>
                        </div>
                      </Link>
                    )}
                    {hasSpringSponsors && springConference && (
                      <Link to={`/events/${springConference.id}/sponsors`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          <span>Sponsors</span>
                        </div>
                      </Link>
                    )}
                  </Sidebar.Collapse>
                </div>
                <div className="flex content-center">
                  <Sidebar.Item href={`page/23`} icon={HiStop}>
                    <div className="">Agritechnica</div>
                  </Sidebar.Item>
                </div>
                <div className="flex content-center">
                  <Link to="/partner-opportunities" className="w-full">
                    <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal />
                      <span>Partner Opportunities</span>
                    </div>
                  </Link>
                </div>
                {awardsVotingOpen && (
                  <div className="flex content-center">
                    <Sidebar.Collapse icon={HiStar} label={"Awards Voting"}>
                      <Link to="/members/awards/vote">
                        <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                          <HiDotsCircleHorizontal width={16} />
                          <span>Vote</span>
                        </div>
                      </Link>
                    </Sidebar.Collapse>
                  </div>
                )}
                <div className="flex content-center">
                  <Sidebar.Collapse
                    icon={HiFolderOpen}
                    label={"Member Resources"}
                  >
                    {checkAdminAccess() && (
                      <Link to={`/members/resources`}>
                        <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                          <HiDotsCircleHorizontal width={16} />
                          <span>Manage</span>
                        </div>
                      </Link>
                    )}
                    <Link to="members/directory">
                      <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                        <HiDotsCircleHorizontal width={16} />
                        <span>Member Directory</span>
                      </div>
                    </Link>
                    <Link to="member/files">
                      <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                        <HiDotsCircleHorizontal width={16} />
                        <span>Download Files</span>
                      </div>
                    </Link>
                    {resourcePages &&
                      resourcePages.length > 0 &&
                      resourcePages.map((page) => (
                        <Link key={page.id} to={`/page/${page.id}`}>
                          <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                            <HiDotsCircleHorizontal width={16} />
                            <span>{page.title}</span>
                          </div>
                        </Link>
                      ))}
                  </Sidebar.Collapse>
                </div>
                {providerPages.length > 0 && (
                  <div className="flex content-center">
                    <Sidebar.Collapse icon={HiUsers} label={"Providers"}>
                      {providerPages.map((page) => (
                        <Link key={page.id} to={`/page/${page.id}`}>
                          <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                            {page.title}
                          </div>
                        </Link>
                      ))}
                    </Sidebar.Collapse>
                  </div>
                )}
                <div className="flex content-center">
                  <Link to="/members/tags" className="w-full">
                    <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <MdTag />
                      <span>Search By Tags</span>
                    </div>
                  </Link>
                </div>
                <div className="flex content-center">
                  <Link to="/users" className="w-full">
                    <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiUsers />
                      <span>User List</span>
                    </div>
                  </Link>
                </div>
                {checkAdminAccess() && (
                  <div className="flex content-center">
                    <Sidebar.Collapse icon={HiFolderOpen} label={"Admin"}>
                      <Link to={`/members/resources`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Manage Resources
                        </div>
                      </Link>
                      <Link to={`/admin/pages`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Pages
                        </div>
                      </Link>
                      <Link to={`/admin/files`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Files
                        </div>
                      </Link>
                      <Link to={`/admin/ads`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Ads
                        </div>
                      </Link>
                      <Link to={`/admin/events`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Events
                        </div>
                      </Link>
                      <Link to={`/admin/sponsors`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Event Sponsors
                        </div>
                      </Link>
                      <Link to={`/admin/forms`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Forms
                        </div>
                      </Link>
                      <Link to={`/admin/newsletters`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Newsletters
                        </div>
                      </Link>
                      <Link to={`/admin/emails`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Emails Sent
                        </div>
                      </Link>
                      <Link to={`/admin/push-notifications`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Push Notifications
                        </div>
                      </Link>
                      <Link to={`/admin/quickbooks`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          QuickBooks
                        </div>
                      </Link>
                      <Link to={`/admin/inactive-members`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Inactive Members
                        </div>
                      </Link>
                      <Link to={`/admin/member-engagement`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Member Engagement
                        </div>
                      </Link>
                      <Link to={`/admin/partner-opportunities`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Partner Opportunities
                        </div>
                      </Link>
                      <Link to={`/admin/partner-opportunities/regions`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Partner Opportunity Regions
                        </div>
                      </Link>
                      <Link to={`/admin/awards`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          Awards Voting
                        </div>
                      </Link>
                      <Link to={`/users/fema`}>
                        <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                          FEMA Passwords
                        </div>
                      </Link>
                    </Sidebar.Collapse>
                  </div>
                )}
              </Sidebar.ItemGroup>
            </Sidebar.Items>
          </Sidebar>
        </Drawer.Items>
      </Drawer>
      <Sidebar className="[&>div]:bg-transparent [&>div] hidden lg:block">
        <Sidebar.Items>
          <Sidebar.ItemGroup>
            <div className="flex content-center">
              <Link to="/" className="w-full">
                <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                  <HiHome />
                  <span>Dashboard</span>
                </div>
              </Link>
            </div>
            <div className="flex content-center">
              <Link to="/schedule" className="w-full">
                <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                  <HiOutlineClock />
                  <span>My Schedule</span>
                </div>
              </Link>
            </div>
            <div className="flex content-center">
              <Link to="/members" className="w-full">
                <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                  <HiOutlineCollection />
                  <span>Member List</span>
                </div>
              </Link>
            </div>
            {userModel &&
              userModel.userLevelId !== UserLevelNames.SuperAdmin && (
                <>
                  <div className="flex content-center">
                    <Sidebar.Item
                      href={`/member/${userModel?.memberId}`}
                      icon={HiOutlineIdentification}
                    >
                      <div className="">Member Profile</div>
                    </Sidebar.Item>
                  </div>
                  <div className="flex content-center">
                    <Sidebar.Item
                      href="/member/my-page"
                      icon={HiFolderOpen}
                    >
                      <div className="">My Showcase Page</div>
                    </Sidebar.Item>
                  </div>
                </>
              )}
            <div className="flex content-center">
              <Sidebar.Collapse
                icon={HiOutlineCalendar}
                label={"Fall Convention"}
              >
                <Link
                  to={
                    fallConference?.sitePageId
                      ? `/page/${fallConference.sitePageId}`
                      : `/members/fallconvention`
                  }
                >
                  <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                    <HiDotsCircleHorizontal width={16} />
                    <span>Information</span>
                  </div>
                </Link>
                <Link to="/members/industryshowcase">
                  <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                    <HiDotsCircleHorizontal width={16} />
                    <span>Industry Showcase</span>
                  </div>
                </Link>
                {!hideParticipants && fallConference && (
                  <Link to={`/events/${fallConference.id}/participants`}>
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Participants</span>
                    </div>
                  </Link>
                )}
                {fallConference && (
                  <Link to={`/events/${fallConference.id}/schedule`}>
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Schedule</span>
                    </div>
                  </Link>
                )}
                {hasFallSponsors && fallConference && (
                  <Link to={`/events/${fallConference.id}/sponsors`}>
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Sponsors</span>
                    </div>
                  </Link>
                )}
              </Sidebar.Collapse>
            </div>
            <div className="flex content-center">
              <Sidebar.Collapse icon={HiOutlineSun} label={"Spring Meeting"}>
                <Link
                  to={
                    springConference?.sitePageId
                      ? `/page/${springConference.sitePageId}`
                      : `/members/springmeeting`
                  }
                >
                  <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                    <HiDotsCircleHorizontal width={16} />
                    <span>Meeting Information</span>
                  </div>
                </Link>
                {!hideSpringParticipants && springConference && (
                  <Link to={`/events/${springConference.id}/participants`}>
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Participants</span>
                    </div>
                  </Link>
                )}
                {springConference && (
                  <Link to={`/events/${springConference.id}/schedule`}>
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Schedule</span>
                    </div>
                  </Link>
                )}
                {hasSpringSponsors && springConference && (
                  <Link to={`/events/${springConference.id}/sponsors`}>
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Sponsors</span>
                    </div>
                  </Link>
                )}
              </Sidebar.Collapse>
            </div>
            {/* <div className="flex content-center">
              <Sidebar.Item href={`/page/23`} icon={HiStop}>
                <div className="">Agritechnica</div>
              </Sidebar.Item>
            </div> */}

            <div className="flex content-center">
              <Link to="/partner-opportunities" className="w-full">
                <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                  <HiDotsCircleHorizontal />
                  <span>Partner Opportunities</span>
                </div>
              </Link>
            </div>
            {awardsVotingOpen && (
              <div className="flex content-center">
                <Sidebar.Collapse icon={HiStar} label={"Awards Voting"}>
                  <Link to="/members/awards/vote">
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Vote</span>
                    </div>
                  </Link>
                </Sidebar.Collapse>
              </div>
            )}
            <div className="flex content-center">
              <Sidebar.Collapse icon={HiFolderOpen} label={"Member Resources"}>
                {checkAdminAccess() && (
                  <Link to={`/members/resources`}>
                    <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                      <HiDotsCircleHorizontal width={16} />
                      <span>Manage</span>
                    </div>
                  </Link>
                )}
                <Link to="members/directory">
                  <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                    <HiDotsCircleHorizontal width={16} />
                    <span> Member Directory</span>
                  </div>
                </Link>
                <Link to="member/files">
                  <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                    <HiDotsCircleHorizontal width={16} />
                    <span>Download Files</span>
                  </div>
                </Link>
                {resourcePages &&
                  resourcePages.length > 0 &&
                  resourcePages.map((page) => (
                    <Link key={page.id} to={`/page/${page.id}`}>
                      <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                        <HiDotsCircleHorizontal width={16} />
                        <span>{page.title}</span>
                      </div>
                    </Link>
                  ))}
              </Sidebar.Collapse>
            </div>
            {providerPages.length > 0 && (
              <div className="flex content-center">
                <Sidebar.Collapse icon={HiUsers} label={"Providers"}>
                  {providerPages.map((page) => (
                    <Link key={page.id} to={`/page/${page.id}`}>
                      <div className="w-full ml-6 p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                        <HiDotsCircleHorizontal width={16} />
                        <span>{page.title}</span>
                      </div>
                    </Link>
                  ))}
                </Sidebar.Collapse>
              </div>
            )}
            <div className="flex content-center">
              <Link to="/members/tags" className="w-full">
                <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                  <MdTag />
                  <span>Search By Tags</span>
                </div>
              </Link>
            </div>
            <div className="flex content-center">
              <Link to="/users" className="w-full">
                <div className="w-full p-1 rounded-lg hover:bg-gray-100 flex items-center space-x-2">
                  <HiUsers />
                  <span>User List</span>
                </div>
              </Link>
            </div>

            {checkAdminAccess() && (
              <div className="flex content-center">
                <Sidebar.Collapse icon={HiFolderOpen} label={"Admin"}>
                  <Link to={`/members/resources`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Manage Resources
                    </div>
                  </Link>
                  <Link to={`/admin/pages`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Pages
                    </div>
                  </Link>
                  <Link to={`/admin/files`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Files
                    </div>
                  </Link>
                  <Link to={`/admin/ads`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Ads
                    </div>
                  </Link>
                  <Link to={`/admin/events`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Events
                    </div>
                  </Link>
                  <Link to={`/admin/sponsors`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Event Sponsors
                    </div>
                  </Link>
                  <Link to={`/admin/forms`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Forms
                    </div>
                  </Link>
                  <Link to={`/admin/newsletters`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Newsletters
                    </div>
                  </Link>
                  <Link to={`/admin/emails`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Emails Sent
                    </div>
                  </Link>
                  <Link to={`/admin/push-notifications`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Push Notifications
                    </div>
                  </Link>
                  <Link to={`/admin/quickbooks`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      QuickBooks
                    </div>
                  </Link>
                  <Link to={`/admin/inactive-members`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Inactive Members
                    </div>
                  </Link>
                  <Link to={`/admin/member-engagement`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Member Engagement
                    </div>
                  </Link>
                  <Link to={`/admin/partner-opportunities`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Partner Opportunities
                    </div>
                  </Link>
                  <Link to={`/admin/partner-opportunities/regions`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Partner Opportunity Regions
                    </div>
                  </Link>
                  <Link to={`/admin/awards`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      Awards Voting
                    </div>
                  </Link>
                  <Link to={`/users/fema`}>
                    <div className="w-full ml-6 p-1 text-wrap rounded-lg hover:bg-gray-100">
                      FEMA Passwords
                    </div>
                  </Link>
                </Sidebar.Collapse>
              </div>
            )}
          </Sidebar.ItemGroup>
        </Sidebar.Items>
      </Sidebar>
    </>
  );
}
